import type React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Flex } from '@chakra-ui/react';
import { CenterSpinner, Table } from '@/components/ats';
import { KModal, KText } from '@/ui-library/components';
import { useDispatch, useSelector } from 'react-redux';
import type { DrilldownModalProps } from './types';
import { EmptyReportChart } from '@/pages/reports/custom-reports/components/ReportCharts/EmptyReportChart';
import { DrilldownFilter } from './DrilldownFilter';
import { DrilldownHeader } from './DrilldownHeader';
import { useBottomScrollListener } from 'react-bottom-scroll-listener';
import { throttle } from 'lodash-es';
import { getDrilldownContext } from './getDrilldownContext';
import { WHITESPACE_GLOBAL_REGEX } from '@/constants/regex';

interface DrilldownStoreSlice {
  drilldownData?: unknown[];
  isDrillLoading?: boolean;
  currentDrillPage?: number;
  totalCount?: number;
  searchTerm?: string;
  initialLoading?: boolean;
}

type RootState = Record<string, DrilldownStoreSlice | undefined>;
const INTERVIEW_DRILLDOWN_HEADINGS = {
  COMPLETED_INTERVIEWS: 'Completed interviews',
  TOTAL_INTERVIEWS: 'Total interviews',
  COMPLETED_INTERVIEWS_TREND: 'Completed interviews trend',
  COMPLETED_INTERVIEWS_BY_FEEDBACK: 'Completed interviews by feedback',
  COMPLETED_INTERVIEWS_BY_DURATION: 'Completed interviews by duration',
  INTERVIEWED_APPLICANTS: 'Interviewed applicants',
  APPLICANTS_INTERVIEWED: 'Applicants interviewed',
} as const;

const COMPLETED_INTERVIEW_NOTE_HEADINGS: string[] = [
  INTERVIEW_DRILLDOWN_HEADINGS.COMPLETED_INTERVIEWS,
  INTERVIEW_DRILLDOWN_HEADINGS.TOTAL_INTERVIEWS,
  INTERVIEW_DRILLDOWN_HEADINGS.COMPLETED_INTERVIEWS_TREND,
  INTERVIEW_DRILLDOWN_HEADINGS.COMPLETED_INTERVIEWS_BY_FEEDBACK,
  INTERVIEW_DRILLDOWN_HEADINGS.COMPLETED_INTERVIEWS_BY_DURATION,
];

const INTERVIEW_DRILLDOWN_NOTES = {
  COMPLETED_INTERVIEWS:
    'Completed interview count may differ from the row count because an interview can have multiple interviewers',
  INTERVIEWED_APPLICANTS:
    'Interviewed applicant count may differ from the row count because an interview can have multiple interviewers',
  APPLICANTS_INTERVIEWED:
    'Applicants interviewed count may differ from the row count because an interview can have multiple interviewers',
} as const;

const isEmptyObject = (value) => {
  return value && typeof value === 'object' && JSON.stringify(value) === '{}';
};

const getGroupValue = (groupValue) => {
  if (groupValue?.id) return groupValue.id;
  if (groupValue?.id === null) return null;

  if (isEmptyObject(groupValue)) return null;
  return groupValue || null;
};

const drilldownTableColStyles = {
  title: 'Select default columns',
  triggerContainer: {
    position: 'unset',
  },
  trigger: {
    width: '32px',
    height: '32px',
    top: '9px',
    right: '0',
    position: 'absolute',
    borderRadius: '6px',
    zIndex: 10,
    bgColor: 'primary-100',
    border: '1px solid',
    borderColor: 'primary-200',
  },
  triggerIcon: {
    width: '16px',
    height: '16px',
    color: 'primary-600',
  },
  popoverContent: {
    maxH: '400px',
    zIndex: 9999,
  },
};

type DrilldownModalTableColStyles = typeof drilldownTableColStyles & {
  triggerPortalRef?: React.RefObject<HTMLDivElement | null>;
  triggerContainer?: { position: string };
  trigger?: Record<string, string>;
};

const getDrilldownTableId = (storeSlice: string, drilldownHeading?: string) => {
  const baseId = `drilldown-table-${storeSlice}`;
  if (!drilldownHeading) return baseId;
  const headingKey = drilldownHeading.replace(WHITESPACE_GLOBAL_REGEX, '-').toLowerCase();
  return `${baseId}-${headingKey}`;
};

const DrilldownBody = ({
  isDrillLoading,
  currentDrillPage,
  drilldownData,
  totalCount,
  columnConfig,
  handleLoadMore,
  modalSize,
  initialLoading,
  isSortRefetching = false,
  enableColumnConfig = true,
  tableColStyles = drilldownTableColStyles,
  tableId,
  isColumnLevelSorting = false,
  defaultSortBy,
  getTableState,
}) => {
  /* Attach to table scroll container only (tableRef) – modal does not scroll, header stays sticky. */
  const scrollRef = useBottomScrollListener(handleLoadMore, {
    offset: 400,
    debounce: 100,
  });

  const scrollHeight =
    modalSize === 'full' ? 'calc(100vh - 100px)' : 'calc(100vh - 300px)';

  return (
    <Flex height={scrollHeight} overflow="hidden" flexDir="column" p="0px 24px">
      {initialLoading ||
      (drilldownData?.length === 0 && (isDrillLoading || isSortRefetching)) ? (
        <CenterSpinner h="100%" />
      ) : drilldownData?.length === 0 ? (
        <EmptyReportChart text="No data found" subText=" " />
      ) : (
        <Flex
          flexDir="column"
          flex={1}
          position="relative"
          minH={0}
          overflow="hidden"
        >
          <Table
            tableRef={scrollRef as React.RefObject<HTMLDivElement>}
            tableId={tableId}
            columns={columnConfig}
            data={drilldownData}
            skipPagination={true}
            pagination={{
              total: totalCount,
              count: drilldownData?.length,
              limit: 50,
            }}
            enableLoadMore={false}
            paginationLimit={50}
            page={{ number: currentDrillPage }}
            applyPage={handleLoadMore}
            enableColumnConfig={enableColumnConfig}
            tableColStyles={tableColStyles}
            isColumnLevelSorting={isColumnLevelSorting}
            manualSortBy
            defaultSortBy={defaultSortBy}
            getTableState={getTableState}
            layoutStyles={{
              borderBottomWidth: '0px',
            }}
            layoutTableStyles={{
              marginBottom: '50px',
            }}
          />
          {isDrillLoading && drilldownData?.length >= 50 && (
            <CenterSpinner h="50px" />
          )}
        </Flex>
      )}
    </Flex>
  );
};

export const DrilldownModal: React.FC<DrilldownModalProps> = ({
  isOpen,
  onClose,
  timeFrame,
  group,
  filters,
  drilldownMetric,
  drilldownHeading,
  columnAccessor,
  dt,
  g1,
  g2,
  xAxis,
  yAxis,
  type,
  config,
  g1Header,
  g2Header,
  tableHeader,
  chartValue,
  metricValue,
  tile,
  deletedTag,
  isG1Deleted,
  isG2Deleted,
}) => {
  const dispatch = useDispatch();
  const columnSettingsSlotRef = useRef(null);
  const [metric, setMetric] = useState(drilldownMetric);
  const [modalSize, setModalSize] = useState('5xl');
  const drilldownData = useSelector(
    (state: RootState) => state[config.storeSlice]?.drilldownData || []
  );
  const isDrillLoading = useSelector(
    (state: RootState) => state[config.storeSlice]?.isDrillLoading
  );
  const currentDrillPage = useSelector(
    (state: RootState) => state[config.storeSlice]?.currentDrillPage
  );
  const totalCount = useSelector(
    (state: RootState) => state[config.storeSlice]?.totalCount
  );
  const searchTerm = useSelector(
    (state: RootState) => state[config.storeSlice]?.searchTerm
  );

  const initialLoading = useSelector(
    (state: RootState) => state[config.storeSlice]?.initialLoading
  );

  const [sortParams, setSortParams] = useState({
    sort_by_column: columnAccessor,
    sort_by_direction: 'asc',
  });
  const [isSortRefetching, setIsSortRefetching] = useState(false);
  const appliedTimeInStageDefaultRef = useRef(false);

  const { columnConfig } = config.useColumnConfig({
    drilldownHeading: drilldownHeading,
    onSort: setSortParams,
    currentSort: sortParams,
  });

  const hasTimeInStageColumn = columnConfig.some(
    (col) => col.accessor === 'time_in_stage'
  );

  const handleDrilldownTableState = (tableState: {
    sortBy?: Array<{ id: string; desc: boolean }>;
  }) => {
    const sortBy = tableState?.sortBy;
    if (!sortBy?.length) return;
    const next = sortBy[0];
    const nextCol = next.id;
    const nextDir = next.desc ? 'desc' : 'asc';
    if (
      nextCol === sortParams.sort_by_column &&
      nextDir === sortParams.sort_by_direction
    ) {
      return;
    }
    setIsSortRefetching(true);
    dispatch(config.resetDrill({ keepSearchTerm: true }));
    setSortParams({ sort_by_column: nextCol, sort_by_direction: nextDir });
  };

  const displayedItems = Math.min(currentDrillPage * 50, totalCount);

  // Reset sort-refetch flag when drilldown load completes (avoids stale UI state)
  useEffect(() => {
    if (!isDrillLoading && isSortRefetching) {
      setIsSortRefetching(false);
    }
  }, [isDrillLoading, isSortRefetching]);

  useEffect(() => {
    if (!isOpen) {
      setModalSize('5xl');
      appliedTimeInStageDefaultRef.current = false;
      return;
    }
    if (!drilldownMetric) return;

    // When time_in_stage column exists, default to it desc only on first open (not when user has changed sort).
    if (
      hasTimeInStageColumn &&
      sortParams.sort_by_column !== 'time_in_stage' &&
      !appliedTimeInStageDefaultRef.current
    ) {
      appliedTimeInStageDefaultRef.current = true;
      setSortParams({
        sort_by_column: 'time_in_stage',
        sort_by_direction: 'desc',
      });
      return;
    }

    const apiMetric =
      config.metricMapping?.[drilldownMetric] || drilldownMetric;
    setMetric(apiMetric);

    dispatch(
      config.fetchDrilldownAction({
        timeFrame,
        group,
        filters,
        drilldown: {
          metric: apiMetric,
          g1: g1?.id || g1,
          g2: g2?.id || g2,
          dt,
          search_term: searchTerm ?? '',
          page: 1,
          sort_by_direction: sortParams.sort_by_direction,
          sort_by_column: sortParams.sort_by_column,
        },
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, searchTerm, sortParams]);

  const handleMore = throttle(() => {
    if (isDrillLoading) return;
    if (displayedItems >= totalCount) return;

    dispatch(
      config.fetchDrilldownAction({
        timeFrame,
        group,
        filters,
        drilldown: {
          metric,
          g1: g1?.id || g1,
          g2: g2?.id || g2,
          dt,
          search_term: searchTerm,
          page: currentDrillPage + 1,
          sort_by_direction: sortParams.sort_by_direction,
          sort_by_column: sortParams.sort_by_column,
        },
      })
    );
  }, 1000);

  const getModalWidth = () => {
    if (modalSize === 'full') return '100vw';

    // Calculate total width by summing the minWidth of each column
    const totalWidth = columnConfig.reduce(
      (sum, column) => sum + (column.minWidth || 200),
      0
    );

    // Add padding/margin space
    const widthWithPadding = totalWidth + 100;

    // Cap the max width
    const maxWidth = Math.min(widthWithPadding, 1200);

    return `${maxWidth}px`;
  };

  const renderFooter = () => {
    return (
      <Flex
        justifyContent="space-between"
        p="16px 32px"
        width="100%"
        alignItems="center"
        borderTopWidth="1px"
        borderColor="border-color"
        height="50px"
      >
        {config.storeSlice === 'interviewReports' &&
          (COMPLETED_INTERVIEW_NOTE_HEADINGS.includes(drilldownHeading) ||
            COMPLETED_INTERVIEW_NOTE_HEADINGS.includes(yAxis)) && (
            <KText
              maxW={modalSize === 'full' ? '' : 'calc(100% - 150px)'}
              isTruncated
              enableTooltip={true}
            >
              <KText display="inline" size="sm-semi-bold">
                Note:{' '}
              </KText>
              {INTERVIEW_DRILLDOWN_NOTES.COMPLETED_INTERVIEWS}
            </KText>
          )}
        {config.storeSlice === 'interviewReports' &&
          drilldownHeading ===
            INTERVIEW_DRILLDOWN_HEADINGS.INTERVIEWED_APPLICANTS && (
            <KText
              maxW={modalSize === 'full' ? '' : 'calc(100% - 150px)'}
              isTruncated
              enableTooltip={true}
            >
              <KText display="inline" size="sm-semi-bold">
                Note:{' '}
              </KText>
              {INTERVIEW_DRILLDOWN_NOTES.INTERVIEWED_APPLICANTS}
            </KText>
          )}
        {config.storeSlice === 'interviewReports' &&
          drilldownHeading ===
            INTERVIEW_DRILLDOWN_HEADINGS.APPLICANTS_INTERVIEWED && (
            <KText
              maxW={modalSize === 'full' ? '' : 'calc(100% - 150px)'}
              isTruncated
              enableTooltip={true}
            >
              <KText display="inline" size="sm-semi-bold">
                Note:{' '}
              </KText>
              {INTERVIEW_DRILLDOWN_NOTES.APPLICANTS_INTERVIEWED}
            </KText>
          )}
        {initialLoading ? null : (
          <Flex justifyContent="flex-end" flex="1">
            <KText size="sm-medium">
              {displayedItems} out of {totalCount}
            </KText>
          </Flex>
        )}
      </Flex>
    );
  };

  const drilldownContext = {
    type,
    metric: drilldownHeading,
    yAxis: yAxis,
    xAxis: xAxis,
    g1: dt || g1,
    g2: g2,
    totalCount,
    g1Header: g1Header,
    g2Header: g2Header,
    tableHeader: tableHeader,
    chartValue: chartValue,
    metricValue: metricValue,
    deletedTag: deletedTag,
    isG1Deleted: isG1Deleted,
    isG2Deleted: isG2Deleted,
  };

  const { metric_name, dt_display_name, g1_display_name, g2_display_name } =
    getDrilldownContext({ context: drilldownContext });

  return (
    <KModal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        dispatch(config?.resetDrill());
      }}
      modalHeaderProps={{
        p: '12px 12px 0px 12px !important',
      }}
      closeOnOverlayClick={true}
      modalContentProps={{
        minW: modalSize === 'full' ? '100vw' : getModalWidth(),
        minHeight: modalSize === 'full' ? '100vh' : '400px',
        maxW: modalSize === 'full' ? '100vw' : '800px',
        maxH: modalSize === 'full' ? '100vh' : '80vh',
        borderRadius: modalSize === 'full' ? '0px' : '16px',
        bg: 'white',
      }}
      title={
        <Flex flexDir="column" gap="10px">
          <DrilldownHeader
            drilldownContext={drilldownContext}
            modalSize={modalSize}
            setModalSize={setModalSize}
          />
          <DrilldownFilter
            dispatch={dispatch}
            config={config}
            searchTerm={searchTerm}
            drilldownData={drilldownData}
            initialLoading={initialLoading}
            columnSettingsSlotRef={columnSettingsSlotRef}
            reportData={{
              timeFrame,
              group: {
                group_by_1: getGroupValue(group?.group_by_1),
                group_by_2: getGroupValue(group?.group_by_2),
              },
              filters,
              tile: tile,
              query_type: 'drilldown',
              drilldown: {
                metric: drilldownMetric,
                g1: g1?.id || g1,
                g2: g2?.id || g2,
                dt,
                page: 1,
                sort_by_direction: sortParams.sort_by_direction,
                sort_by_column: sortParams.sort_by_column,
                metric_name: metric_name,
                g1_display_name: g1_display_name,
                g2_display_name: g2_display_name,
                dt_display_name: dt_display_name,
              },
            }}
          />
        </Flex>
      }
      footer={renderFooter()}
      modalBodyProps={{ p: '0px', flex: '1', overflow: 'hidden' }}
    >
      <>
        <DrilldownBody
          isDrillLoading={isDrillLoading}
          currentDrillPage={currentDrillPage}
          drilldownData={drilldownData}
          totalCount={totalCount}
          columnConfig={columnConfig}
          handleLoadMore={handleMore}
          modalSize={modalSize}
          initialLoading={initialLoading}
          isSortRefetching={isSortRefetching}
          tableId={getDrilldownTableId(config.storeSlice, drilldownHeading)}
          isColumnLevelSorting={true}
          defaultSortBy={[
            {
              id: sortParams.sort_by_column,
              desc: sortParams.sort_by_direction === 'desc',
            },
          ]}
          getTableState={handleDrilldownTableState}
          tableColStyles={
            {
              ...drilldownTableColStyles,
              triggerPortalRef: columnSettingsSlotRef,
              triggerContainer: {
                ...drilldownTableColStyles.triggerContainer,
                position: 'unset',
              },
              trigger: {
                ...drilldownTableColStyles.trigger,
                position: 'relative',
                top: 'unset',
                right: 'unset',
              },
            } as DrilldownModalTableColStyles
          }
        />
      </>
    </KModal>
  );
};
